/**
 * BillingProviderLauncher — handles a BillingLaunch returned from the
 * backend's provider-neutral mutations (startSubscription /
 * startPaymentMethodUpdate).
 *
 * For Stripe launches it redirects the browser to Stripe's hosted
 * Checkout / Billing Portal URL via `window.location.href`. The user
 * completes payment on Stripe's domain and is redirected back to
 * STRIPE_SUCCESS_URL / STRIPE_CANCEL_URL (configured server-side).
 *
 * For Moneris launches it invokes an optional `onMonerisLaunch` callback.
 * The existing BillingDashboard mounts <MonerisTokenization> directly
 * with its own state machine, so this launcher does not try to
 * replicate that integration in Phase 3c. Phase 3c2 will extend this
 * component to mount MonerisTokenization inline once the existing
 * dashboard state (tokenizationTicket, tokenizationUrl, etc.) has been
 * lifted into a reusable shape.
 */

import { useEffect, useRef } from 'react';

export type StripeCheckoutLaunch = {
  __typename?: 'StripeCheckoutLaunch';
  kind: 'stripe_checkout';
  provider: 'stripe';
  sessionId: string;
  url: string;
  mode: string;
  expiresAt?: string | null;
};

export type MonerisModalLaunch = {
  __typename?: 'MonerisModalLaunch';
  kind: 'moneris_modal';
  provider: 'moneris';
  ticket: string;
  checkoutId: string;
  environment: 'qa' | 'prod';
};

export type BillingLaunch = StripeCheckoutLaunch | MonerisModalLaunch;

interface BillingProviderLauncherProps {
  /**
   * A launch payload returned from a provider-neutral billing mutation.
   * When null, the launcher is idle.
   */
  launch: BillingLaunch | null;

  /**
   * Callback invoked before a Stripe redirect fires. Use this to show a
   * loading overlay or disable UI while the browser navigates away.
   */
  onStripeRedirect?: (launch: StripeCheckoutLaunch) => void;

  /**
   * Callback invoked when a Moneris launch payload arrives. The parent
   * component is responsible for mounting MonerisTokenization — this
   * callback just hands the ticket/checkoutId over. See Phase 3c2 note
   * above for why we don't self-mount Moneris here.
   */
  onMonerisLaunch?: (launch: MonerisModalLaunch) => void;

  /**
   * Callback invoked when a launch arrives but we don't know how to
   * handle it (e.g., future provider). Parent can show a message.
   */
  onUnsupported?: (launch: BillingLaunch) => void;
}

export function isStripeLaunch(launch: BillingLaunch): launch is StripeCheckoutLaunch {
  return launch.kind === 'stripe_checkout';
}

export function isMonerisLaunch(launch: BillingLaunch): launch is MonerisModalLaunch {
  return launch.kind === 'moneris_modal';
}

/**
 * Imperative helper: fire a launch without mounting the component.
 * Useful inside async handlers where a side-effecting redirect is
 * the simplest expression of the intent.
 *
 * For Stripe: navigates away via window.location.href.
 * For Moneris: invokes the caller-supplied handler; returns false if
 * none was provided so callers can fall back.
 */
export function executeBillingLaunch(
  launch: BillingLaunch,
  handlers: {
    onMoneris?: (launch: MonerisModalLaunch) => void;
    onUnsupported?: (launch: BillingLaunch) => void;
  } = {}
): boolean {
  if (isStripeLaunch(launch)) {
    window.location.href = launch.url;
    return true;
  }
  if (isMonerisLaunch(launch)) {
    if (handlers.onMoneris) {
      handlers.onMoneris(launch);
      return true;
    }
    return false;
  }
  handlers.onUnsupported?.(launch);
  return false;
}

/**
 * Declarative wrapper. Mount this anywhere in the tree and it will
 * react to `launch` prop changes by firing the appropriate handler
 * exactly once per distinct launch (keyed on a Stripe sessionId or
 * Moneris ticket).
 */
const BillingProviderLauncher: React.FC<BillingProviderLauncherProps> = ({
  launch,
  onStripeRedirect,
  onMonerisLaunch,
  onUnsupported
}) => {
  // Track which launches we've already acted on. A parent that mounts
  // us inside a re-rendering dashboard would otherwise redirect twice
  // or re-open a Moneris modal on every render.
  const firedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!launch) return;
    const key = isStripeLaunch(launch)
      ? `stripe:${launch.sessionId}`
      : isMonerisLaunch(launch)
      ? `moneris:${launch.ticket}`
      : `unknown:${JSON.stringify(launch)}`;

    if (firedKeyRef.current === key) return;
    firedKeyRef.current = key;

    if (isStripeLaunch(launch)) {
      onStripeRedirect?.(launch);
      // Defer by a tick so parent can finish rendering its loading UI
      // before we navigate away.
      setTimeout(() => {
        window.location.href = launch.url;
      }, 0);
      return;
    }
    if (isMonerisLaunch(launch)) {
      onMonerisLaunch?.(launch);
      return;
    }
    onUnsupported?.(launch);
  }, [launch, onStripeRedirect, onMonerisLaunch, onUnsupported]);

  // Render nothing — the work happens in the effect above.
  return null;
};

export default BillingProviderLauncher;
